/**
 * Cart Address Dropdown
 * Handles searchable dropdown functionality for city and street inputs
 */

(function() {
  'use strict';

  window.CartAddressDropdown = {
    /**
     * Enhance searchable inputs with dropdown functionality
     */
    enhance(container, data, validation, sync, previousCityValue) {
      // No dropdown functionality needed since city/street inputs have been removed
      return;

      // Get or create datalist elements
      let streetDatalist = container.querySelector('#street-list');
      if (!streetDatalist && streetInput.hasAttribute('list')) {
        streetDatalist = document.createElement('datalist');
        streetDatalist.id = 'street-list';
        streetInput.parentNode.insertBefore(streetDatalist, streetInput.nextSibling);
      }
      
      let cityDatalist = container.querySelector('#city-list');
      if (!cityDatalist && cityInput.hasAttribute('list')) {
        cityDatalist = document.createElement('datalist');
        cityDatalist.id = 'city-list';
        cityInput.parentNode.insertBefore(cityDatalist, cityInput.nextSibling);
      }

      // Populate city datalist
      if (cityDatalist) {
        const cities = data.getCities();
        cities.forEach(city => {
          const option = document.createElement('option');
          option.value = city;
          cityDatalist.appendChild(option);
        });
      }

      // Create hint element for street input
      const streetField = streetInput.closest('.cart-address-field');
      let streetHint = streetField?.querySelector('.cart-address-street-hint');
      if (!streetHint && streetField) {
        streetHint = document.createElement('span');
        streetHint.className = 'cart-address-street-hint';
        const inputWrapper = streetField.querySelector('.cart-address-input-wrapper');
        if (inputWrapper) {
          inputWrapper.parentNode.insertBefore(streetHint, inputWrapper.nextSibling);
        } else {
          streetField.appendChild(streetHint);
        }
      }

      // Update street dropdown based on selected city
      const updateStreetDropdown = (selectedCity) => {
        if (!streetDatalist) return;
        streetDatalist.innerHTML = '';
        
        if (selectedCity) {
          const streets = data.getStreetsForCity(selectedCity);
          streets.forEach(street => {
            const option = document.createElement('option');
            option.value = street;
            streetDatalist.appendChild(option);
          });
        }
      };

      // Clear street with animation
      const clearStreetWithAnimation = (showHint = true) => {
        if (sync.isPrefilling) {
          streetInput.value = '';
          return;
        }
        
        if (!streetInput.value.trim()) return;
        
        streetInput.classList.add('clearing');
        
        setTimeout(() => {
          streetInput.value = '';
          streetInput.classList.remove('clearing');
          streetInput.classList.add('city-changed');
          
          setTimeout(() => {
            streetInput.classList.remove('city-changed');
          }, 600);
          
          if (showHint && cityInput.value.trim()) {
            streetHint.textContent = 'אנא בחר רחוב מהרשימה';
            streetHint.classList.add('show');
            
            const hideHint = () => {
              streetHint.classList.remove('show');
              streetInput.removeEventListener('focus', hideHint);
              streetInput.removeEventListener('input', hideHint);
            };
            
            setTimeout(hideHint, 3000);
            streetInput.addEventListener('focus', hideHint, { once: true });
            streetInput.addEventListener('input', hideHint, { once: true });
          }
          
          validation.clearError(streetInput);
          streetInput.dispatchEvent(new Event('input', { bubbles: true }));
        }, 150);
      };

      // Update street input state
      const updateStreetInputState = (wasCityChanged = false) => {
        const cityValue = cityInput.value.trim();
        const cityChanged = cityValue !== previousCityValue.value;
        
        if (!cityValue) {
          streetInput.disabled = true;
          streetInput.placeholder = '* רחוב';
          streetHint.classList.remove('show');
          
          if (streetInput.value.trim()) {
            clearStreetWithAnimation(false);
          } else {
            streetInput.value = '';
          }
          
          updateStreetDropdown('');
        } else {
          const hadStreetValue = !!streetInput.value.trim();
          
          if (wasCityChanged && cityChanged && hadStreetValue) {
            clearStreetWithAnimation(true);
          }
          
          streetInput.disabled = false;
          streetInput.placeholder = 'רחוב *';
          updateStreetDropdown(cityValue);
          // Close any open street dropdown so the next open reflects the new city
          streetInput._cartDropdown?.hide?.();
          validation.validateCity(cityInput, data);
          
          if (wasCityChanged && cityChanged) {
            setTimeout(() => {
              streetInput.focus();
            }, 200);
          }
        }
        
        previousCityValue.value = cityValue;
      };

      // Initialize
      if (!previousCityValue.value) {
        previousCityValue.value = cityInput.value.trim();
      }
      updateStreetInputState();

      // City change handlers
      cityInput.addEventListener('change', () => {
        const cityChanged = cityInput.value.trim() !== previousCityValue.value;
        updateStreetInputState(cityChanged);
        validation.validateCity(cityInput, data);
      });

      cityInput.addEventListener('input', () => {
        const cityValue = cityInput.value.trim();
        const cityChanged = cityValue !== previousCityValue.value;
        
        if (cityValue && data.getStreetsForCity(cityValue).length > 0) {
          updateStreetDropdown(cityValue);
          updateStreetInputState(cityChanged);
        } else if (!cityValue) {
          updateStreetInputState(cityChanged);
        } else {
          streetHint.classList.remove('show');
        }
      });

      cityInput.addEventListener('blur', () => {
        const cityValue = cityInput.value.trim();
        const cityChanged = cityValue !== previousCityValue.value;
        
        if (cityValue && data.getStreetsForCity(cityValue).length > 0) {
          updateStreetInputState(cityChanged);
        } else if (!cityValue) {
          updateStreetInputState(cityChanged);
        }
      });

      // Street validation handlers
      streetInput.addEventListener('blur', () => {
        if (streetInput.value.trim()) {
          validation.validateStreet(streetInput, cityInput, data);
        }
      });

      streetInput.addEventListener('input', () => {
        if (streetInput.value.trim()) {
          validation.validateStreet(streetInput, cityInput, data);
        } else {
          validation.clearError(streetInput);
        }
      });

      // Create custom dropdown for city
      this.createDropdown(
        cityInput,
        data.getCities(),
        data.cityCodeMap,
        false,
        container,
        data,
        validation,
        updateStreetInputState,
        previousCityValue,
        streetHint,
        null
      );

      // Create custom dropdown for street (needs cityInput reference)
      this.createDropdown(
        streetInput,
        data.getAllStreets(),
        data.streetCodeMap,
        true,
        container,
        data,
        validation,
        updateStreetInputState,
        previousCityValue,
        streetHint,
        cityInput
      );
    },

    /**
     * Create custom dropdown for an input
     */
    createDropdown(input, values, codeMap, isStreet, container, data, validation, updateStreetInputState, previousCityValue, streetHint, cityInput = null) {
      if (input.dataset.enhanced) return;
      input.dataset.enhanced = '1';

      const wrapper = document.createElement('div');
      wrapper.style.position = 'relative';
      wrapper.className = 'cart-address-dropdown-wrapper';

      const list = document.createElement('ul');
      list.className = 'cart-address-dropdown-list';
      list.style.cssText = `
        position:absolute;
        z-index:1000;
        background:#fff;
        border:1px solid #ccc;
        border-radius:4px;
        width:100%;
        max-height:180px; /* ~5 rows */
        overflow-y:auto;
        overflow-x:hidden;
        display:none;
        list-style:none;
        margin:0;
        padding:0;
        box-shadow:0 2px 8px rgba(0,0,0,0.15);
        top:100%;
        margin-top:4px;
      `;

      input.parentNode.insertBefore(wrapper, input);
      wrapper.appendChild(input);
      wrapper.appendChild(list);

      let isSelecting = false;

      const hideDropdown = () => {
        list.style.display = 'none';
        list.innerHTML = '';
      };

      const showDropdown = (showInitial = false) => {
        if (isSelecting) return;

        if (isStreet && cityInput && !cityInput.value.trim()) {
          validation.showError(input, 'אנא בחר עיר תחילה');
          hideDropdown();
          return;
        }

        const q = input.value.toLowerCase().trim();
        list.innerHTML = '';

        let availableValues = values;
        
        if (isStreet && cityInput && cityInput.value.trim()) {
          const cityStreets = data.getStreetsForCity(cityInput.value.trim());
          availableValues = availableValues.filter(v => cityStreets.includes(v));
        }

        let filtered;
        if (q.length > 0) {
          filtered = availableValues.filter(v => v.toLowerCase().includes(q));
        } else {
          filtered = availableValues;
        }

        if (filtered.length === 0) {
          hideDropdown();
          return;
        }

        filtered.forEach(v => {
          const li = document.createElement('li');
          li.textContent = v;
          li.style.cssText = 'padding:8px 12px;cursor:pointer;border-bottom:1px solid #eee;';
          li.onmouseenter = () => { li.style.background = '#f5f5f5'; };
          li.onmouseleave = () => { li.style.background = '#fff'; };
          
          li.onmousedown = (e) => {
            e.preventDefault();
            isSelecting = true;
            input.value = v;
            hideDropdown();
            
            if (isStreet) {
              validation.validateStreet(input, cityInput, data);
              if (streetHint) streetHint.classList.remove('show');
            } else {
              validation.validateCity(input, data);
              const cityChanged = v !== previousCityValue.value;
              updateStreetInputState(cityChanged);
            }
            
            input.blur();
            
            setTimeout(() => {
              isSelecting = false;
            }, 100);
            
            input.dispatchEvent(new Event('change', { bubbles: true }));
            input.dispatchEvent(new Event('input', { bubbles: true }));
          };
          
          list.appendChild(li);
        });

        list.style.display = 'block';
      };

      input.addEventListener('input', (e) => {
        // Avoid opening on programmatic input events (prefill/validation)
        if (!e.isTrusted) return;
        if (!isSelecting) showDropdown(false);
      });
      
      // Show only when user clicks/taps, not on initial render focus
      input.addEventListener('click', () => {
        if (!isSelecting) showDropdown(true);
      });

      input.addEventListener('blur', (e) => {
        setTimeout(() => {
          if (!wrapper.contains(document.activeElement)) {
            hideDropdown();
          }
        }, 200);
      });

      document.addEventListener('click', e => {
        if (!wrapper.contains(e.target)) {
          hideDropdown();
        }
      });

      // Keyboard navigation
      input.addEventListener('keydown', (e) => {
        const items = Array.from(list.children);
        const currentIndex = items.findIndex(li => li.style.background === 'rgb(245, 245, 245)');
        
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
          items.forEach(li => li.style.background = '#fff');
          if (items[nextIndex]) {
            items[nextIndex].style.background = '#f5f5f5';
            items[nextIndex].scrollIntoView({ block: 'nearest' });
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
          items.forEach(li => li.style.background = '#fff');
          if (items[prevIndex]) {
            items[prevIndex].style.background = '#f5f5f5';
            items[prevIndex].scrollIntoView({ block: 'nearest' });
          }
        } else if (e.key === 'Enter' && currentIndex >= 0 && items[currentIndex]) {
          e.preventDefault();
          isSelecting = true;
          const selectedValue = items[currentIndex].textContent;
          input.value = selectedValue;
          hideDropdown();
          
          if (isStreet) {
            validation.validateStreet(input, cityInput, data);
            if (streetHint) streetHint.classList.remove('show');
          } else {
            validation.validateCity(input, data);
            const cityChanged = selectedValue !== previousCityValue.value;
            updateStreetInputState(cityChanged);
          }
          
          setTimeout(() => {
            isSelecting = false;
          }, 100);
          
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input.dispatchEvent(new Event('input', { bubbles: true }));
        } else if (e.key === 'Escape') {
          e.preventDefault();
          hideDropdown();
          input.blur();
        }
      });

      // Expose simple controls for external refresh/close
      input._cartDropdown = {
        hide: hideDropdown,
        refresh: () => {
          hideDropdown();
          // Re-render only if input has focus to avoid surprising the user
          if (document.activeElement === input) {
            showDropdown(false);
          }
        }
      };
    }
  };
})();

